import { useSelector, useDispatch } from "react-redux";
import { Alert, Snackbar } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import styled, { ThemeProvider } from "styled-components";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { handleCloseAlert } from "./state-management/actions/alerts-actions";
import { AlertColor } from "@mui/material/Alert";
import {
  selectAlertState,
  selectGlobalState,
} from "./state-management/selectors";
import theme from "./config/theme";
import Header from "./components/topnav/topHeader";
import { useLocation } from "react-router-dom"; // Import useLocation

const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

const BodyStyled = styled.header`
  display: flex;
  flex-direction: row-reverse;
  justify-content:flex-end;
  min-height: 100vh;
  margin: auto;
  -webkit-overflow-scrolling: touch;
  flex: auto;
  position: relative;
  max-width: inherit;
`;

function Layout({ ...props }) {
  const dispatch = useDispatch();
  const { theme: themeMode } = useSelector(selectGlobalState);
  const mode = themeMode === "light" ? theme.light : theme.dark;
  const { alert, alertSeverity, alertContext } = useSelector(selectAlertState);
  
  const rtlTheme = createTheme({
    direction: "rtl",
    typography: {
      fontFamily: "IRANSans",
    },
  });
  
  const severity = alertSeverity as AlertColor | undefined;

  const handleClose = () => {
    dispatch(handleCloseAlert());
  };

  const location = useLocation(); // Get current location

  // Define paths where the Header should not be shown
  const hideHeaderPaths = ["/login-page", "/register-page", "/forgot-password"]; 

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={rtlTheme}>
        <BodyStyled
          data-theme={themeMode}
          style={{ backgroundColor: mode.backgroundColor.bgPrimary }}
        >
          {!hideHeaderPaths.includes(location.pathname) && <Header />} {/* Conditional rendering of Header */}
          {alert && alertContext && (
            <Snackbar
              open={alert}
              autoHideDuration={3000}
              onClose={handleClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{
                position: "fixed",
                left: "50%",
                transform: "translateX(-50%)",
                top: 0,
                zIndex: 9999,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                textAlign: "center",
                justifyContent: "center",
                maxWidth: "300px",
                width: "100%",
                direction: "rtl",
              }}
            >
              <Alert
                variant="filled"
                severity={severity}
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  textAlign: "center",
                  justifyContent: "center",
                  fontSize: 16, // Message text size
                  "& .MuiAlert-action": {
                    marginLeft: 8,
                    fontSize: 14, // Action text size
                  },
                }}
              >
                {alertContext}
              </Alert>
            </Snackbar>
          )}
          {props.children}
        </BodyStyled>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default Layout;
